import React, { RefObject } from 'react';
import { Dispatch } from 'redux';

import { IPageInfo } from '@constants/common.interface';
import { TicketApiType } from './ticketlist.constants';

export interface ITicket {
    id: number;
    ticketNumber: string;
    subject: string;
    requestedBy: string;
    createdFor: string;
    workgroup: string;
    status: string;
    priority: string;
    dateOfRequest: string;
    dueDate: string;
    taskStatus?: string;
    archiveFlag?: string;
    allowView?: boolean;
    allowEdit?: boolean;
    allowCancel?: boolean;
    allowMarkAsComplete?: boolean;
    allowClaim?: boolean;
    allowReassign?: boolean;
    allowAssign?: boolean;
    allowReopen?: boolean;
    allowRestart?: boolean;
    allowArchive?: boolean;
}

export interface ISearchCriteria {
    sortBy: string;
    sortOrder: 'ASC' | 'DESC';
    pageSize: number;
}

export interface ITicketHeaderProps {
    fetchDefaultTickets: () => void;
    ticketsCount: number;
    onClaimNewTicket: (workgroup?: string) => void;
    onAddNewTicket: () => void;
    workgroupOptions: { value: string; text: string }[];
    enableTicketSupport: boolean;
    viewTicketPermission: boolean;
    isReportPageRedirect: boolean;
}

export interface ITicketTableProps {
    tickets: ITicket[];
    isAdvanceSearch: boolean;
    searchCriteria: any;
    ticketsCount: number;
    onRowSelect: (selectedRows: any) => void;
    onCompleteTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCompleteVerifyTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReassignTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCancelTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReopenTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    viewTicketPermission: boolean;
}

export interface IAdvanceSearchProps {
    resetTrigger: boolean;
    setResetTrigger: (value: boolean) => void;
    isWatchTicketAllowed: boolean;
}
export interface IuseAdvanceSearchProps {
    setResetTrigger: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface IDefaultSearchProps {
    workgroupOptions: { value: string; text: string }[];
    isInitialLoad: RefObject<boolean>;
    isWatchTicketAllowed: boolean;
}

export interface IuseDefaultSearchProps {
    isInitialLoad: RefObject<boolean>;
}

export interface IuseTicketTableProps {
    isAdvanceSearch: boolean;
    onCompleteTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCompleteVerifyTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReassignTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCancelTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReopenTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    viewTicketPermission: boolean;
}

export interface ITableContext {}

export interface ICancelTicketModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
    onCancelTicketReasonOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
}
export interface IExportTicketsModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
    isAdvanceSearch: boolean;
}

export interface IAddWatchersModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
    selectedRows: string[];
    resetSelection: () => void;
}

export interface IReopenTicketModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
}

export interface IErrorModal {
    errorMessage: string;
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
}

export interface ICancelReasonModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
    isAdvanceSearch: boolean;
}

export interface IMarkVerifyCompleteModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
    isAdvanceSearch: boolean;
}

export interface ICompleteTicketModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
    isAdvanceSearch: boolean;
}

export interface IReassignTicketModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
}

export interface IBulkReassignTicketModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
    selectedRows: string[];
    isAdvanceSearch: boolean;
}

export interface IuseActionPopoverProps {
    onCompleteTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCompleteVerifyTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReassignTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCancelTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReopenTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
}

export enum RequestStatus {
    SUCCESS = 'success',
    FAILURE = 'failure',
}

const filterOptionKeys = [
    'archiveFlagValue',
    'assignedToMe',
    'assignee',
    'assigneeId',
    'customFieldSelect',
    'customFieldSelectVal',
    'requestedBy',
    'requestedByName',
    'requestedBytagId',
    'requester',
    'roleId',
    'submittedAfter',
    'submittedBefore',
    'ticketCategorySelect',
    'ticketComments',
    'ticketNumber',
    'ticketPriority',
    'ticketQueues',
    'ticketStatus',
    'ticketSubject',
    'ticketType',
    'unClaimedTicket',
    'userRole',
    'tagId',
    'workGroupBytagId',
    'watchStatusAdvanced',
] as const;

export type IFilterOptions = Record<(typeof filterOptionKeys)[number], boolean>;

export interface ITicketTableRef {
    navigateToPage: (pageNumber: number) => void;
    resetSelection: () => void;
    resetSorting: () => void;
}

export interface IRefreshTicketList {
    dispatch: Dispatch;
    listApiType: TicketApiType;
    defaultListAPI: (dispatch, payload) => void;
    listAPI: (dispatch, payload) => void;
    filteredListAPI: (dispatch, payload) => void;
}
